import { formatNumber } from '@disy/lib/format';
import { logger } from '@disy/lib/logging';

import { getDisyNamespace } from '../disy';

const NBSP = ' ';

const debugI18n = new URLSearchParams(location.search).has('debugI18n');

// Note: If you do changes here, you probably need to do similar changes to PebbleI18Function.java.
export function loaderI18n(this: Record<string, string>, key = '', options: Record<string, unknown> = {}): string {
  const count = options.count;
  if (typeof count === 'number') {
    const category = selectPluralizationCategory(count);
    const keyCandidate = `${key}_${category}`;
    if (this[keyCandidate]) {
      key = keyCandidate;
    } else {
      key = `${key}_other`;
    }
  }

  if (debugI18n) {
    return key;
  }

  let text = this[key] ?? options.defaultText;
  if (text == null) {
    logger.error(`Unknown message key "${key}"`);
    return getDisyNamespace().debug ? `???${key}???` : '(missing message)';
  }

  // replace three dots with ellipsis
  text = text.replace(/\s*(\.{3,}|…)/g, `${NBSP}…`);

  return Object.entries(options).reduce(
    (result, [option, value]) =>
      result.replace(new RegExp(`{{${option}}}`, 'g'), typeof value === 'number' ? formatNumber(value) : String(value)),
    text,
  );
}

/**
 * Selects the pluralization category of the given number.
 *
 * _Note:_ Although we use the `Intl.LDMLPluralRule` values, we do not use the `Intl.PluralRules` API,
 * because for example with the 'en' locale we want to select 'zero' for 0 instead of 'other'.
 *
 * @param {number} n - A number
 * @return {Intl.LDMLPluralRule} The pluralization category of the number
 */
function selectPluralizationCategory(n: number) {
  switch (n) {
    case 0:
      return 'zero';
    case 1:
      return 'one';
    // Custom: new Intl.PluralRules('de-DE' / 'en_EN').select(2) suggests 'other' instead. 'two' is
    // part of the type Intl.LDMLPluralRule, though, so totally fine.
    case 2:
      return 'two';
  }
  return 'other';
}
